/**
 * Pagination Component
 *
 * Displays pagination controls for product lists.
 * Supports previous/next buttons, page numbers, and results info.
 *
 * @param {object} pagination - Pagination metadata from backend
 * @param {number} pagination.page - Current page (1-indexed from backend)
 * @param {number} pagination.limit - Items per page
 * @param {number} pagination.total - Total items
 * @param {number} pagination.totalPages - Total pages
 * @param {boolean} pagination.hasNextPage - Has next page
 * @param {boolean} pagination.hasPreviousPage - Has previous page
 * @param {number} currentPage - Current page (0-indexed for state)
 * @param {Function} onPageChange - Callback when page changes
 */
function Pagination({ pagination, currentPage, onPageChange }) {
  const { totalPages, hasNextPage, hasPreviousPage, total, limit } = pagination;

  // Don't render if only one page
  if (totalPages <= 1) {
    return null;
  }

  const handlePageClick = (page) => {
    if (page === currentPage) return; // Already on this page

    onPageChange(page);

    // Scroll to top of page smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate smart page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    const pages = [];
    const delta = 1; // Pages on each side of current

    for (let i = 0; i < totalPages; i++) {
      // Always show first page, last page, and pages around current
      if (
        i === 0 || // First page
        i === totalPages - 1 || // Last page
        (i >= currentPage - delta && i <= currentPage + delta) // Around current
      ) {
        pages.push(i);
      } else if (
        pages[pages.length - 1] !== '...' // Don't add consecutive ellipsis
      ) {
        pages.push('...');
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  // Calculate displayed item range with safe defaults to prevent NaN
  const safeLimit = Number(limit) || 12;
  const safeTotal = Number(total) || 0;

  // Calculate the range for display
  const startItem = safeTotal > 0 ? (currentPage * safeLimit) + 1 : 0;
  const endItem = safeTotal > 0 ? Math.min((currentPage + 1) * safeLimit, safeTotal) : 0;

  return (
    <nav aria-label="Product pagination" className="mt-5 mb-4">
      <ul className="pagination justify-content-center">
        {/* Previous Button */}
        <li className={`page-item ${!hasPreviousPage ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => handlePageClick(currentPage - 1)}
            disabled={!hasPreviousPage}
            aria-label="Previous page"
            style={{
              cursor: hasPreviousPage ? 'pointer' : 'not-allowed',
              border: '1px solid #dee2e6',
              padding: '0.5rem 0.75rem'
            }}
          >
            <i className="fa fa-chevron-left"></i> Previous
          </button>
        </li>

        {/* Page Numbers */}
        {pageNumbers.map((page, index) => (
          page === '...' ? (
            <li key={`ellipsis-${index}`} className="page-item disabled">
              <span className="page-link" style={{ border: '1px solid #dee2e6', padding: '0.5rem 0.75rem' }}>
                ...
              </span>
            </li>
          ) : (
            <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageClick(page)}
                aria-label={`Go to page ${page + 1}`}
                aria-current={page === currentPage ? 'page' : undefined}
                style={{
                  cursor: 'pointer',
                  border: '1px solid #dee2e6',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: page === currentPage ? '#03a9f5' : 'white',
                  color: page === currentPage ? 'white' : '#03a9f5',
                  fontWeight: page === currentPage ? 'bold' : 'normal'
                }}
              >
                {page + 1}
              </button>
            </li>
          )
        ))}

        {/* Next Button */}
        <li className={`page-item ${!hasNextPage ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => handlePageClick(currentPage + 1)}
            disabled={!hasNextPage}
            aria-label="Next page"
            style={{
              cursor: hasNextPage ? 'pointer' : 'not-allowed',
              border: '1px solid #dee2e6',
              padding: '0.5rem 0.75rem'
            }}
          >
            Next <i className="fa fa-chevron-right"></i>
          </button>
        </li>
      </ul>

      {/* Results Info */}
      {safeTotal > 0 && (
        <p className="text-center text-muted mt-3" style={{ fontSize: '14px' }}>
          Showing <strong>{startItem}</strong> - <strong>{endItem}</strong> of <strong>{safeTotal}</strong> products
        </p>
      )}
    </nav>
  );
}

export default Pagination;
